import React, { useState, useEffect, useMemo } from 'react'

import {
	authLogin, authRegister, requestResetLink, authReset
} from './api'

/**
 * Derive the API key from user object
 * Hierarchy: user's api_key -> company's default contact person's api_key
 */
const deriveApiKey = (user) => {
	if (!user) return null;
	return user?.api_key !== '' 
		? user?.api_key 
		: user?.memberships?.[0]?.company?.contact_user?.api_key ?? null;
}

/** Keep session user in sync after company settings are saved (e.g. config_settings). */
export const mergeCompanyIntoUser = (user, companyData) => {
	if (!user?.memberships?.length || !companyData) {
		return user
	}

	const config_settings =
		typeof companyData.config_settings === 'string'
			? companyData.config_settings
			: JSON.stringify(companyData.config_settings ?? {})

	return {
		...user,
		memberships: user.memberships.map((membership, index) => {
			const companyId = membership.company?.id ?? membership.company_id
			const shouldUpdate =
				companyData.id == null
					? index === 0
					: companyId == null || Number(companyId) === Number(companyData.id)
			if (!shouldUpdate) {
				return membership
			}
			return {
				...membership,
				company: {
					...membership.company,
					...companyData,
					config_settings,
				},
			}
		}),
	}
}

export const JobAppContext = React.createContext({
	isLogginIn: false,
	isSigningOut: false,
	isAuthenticated: localStorage.getItem('accessToken') ? true : false,
	accessToken: localStorage.getItem('accessToken') ? localStorage.getItem('accessToken') : null,
	user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null,
	apiKey: deriveApiKey(localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null),
	error: '',
})

export default function GlobalState(props){

	const [ isLogginIn, setIsLogginIn ] = useState(false);
	const [ isSigningOut, setIsSigningOut ] = useState(false);
	const [ isAuthenticated, setIsAuthenticated ] = useState(localStorage.getItem('accessToken') ? true : false);
	const [ accessToken, setAccessToken ] = useState(localStorage.getItem('accessToken') ? localStorage.getItem('accessToken') : null);
	const [ user, setUser ] = useState(localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null);
	const [ error, setError ] = useState('');

	// Derive apiKey from user - hierarchy: user's api_key -> company's contact_user's api_key
	const apiKey = useMemo(() => deriveApiKey(user), [user]);

	const localAccessToken = localStorage.getItem('accessToken')
	useEffect(() => {
		if(localAccessToken){
			setAccessToken(localAccessToken);
		} else {
			setAccessToken(null);
			setIsAuthenticated(false);
			setUser(null);
		}
	}, [localAccessToken])


	const auth = credentials => {
		
		setIsLogginIn(true);
		return authLogin(credentials)
			.then(response => {
				if (response.ok === false) {
					throw Error(response)
				}
				return response.json()
			})
			.then(json => {
				localStorage.setItem('accessToken', json.token)
				localStorage.setItem('user', JSON.stringify(json.user))

				setAccessToken(json.token);
				setUser(json.user);
				setIsAuthenticated(true);
				// dispatch({
				// 	type: LOGIN.SUCCESS,
				// 	payload: json,
				// })
			})
			.catch(error => {
				setError(error.message);
				// dispatch({
				// 	type: LOGIN.ERROR,
				// 	payload: error,
				// })
			})
	}
	
	const signOut = () => {
		setIsSigningOut(true);	
		localStorage.removeItem('accessToken')
		localStorage.removeItem('user')

		setIsAuthenticated(false);
		setAccessToken(null);
		setUser(null);

		// localStorage.removeItem('role')
		// localStorage.removeItem('company')

		setIsSigningOut(false);
	}
	
	const register = data => {

		return authRegister(data).then(response => {
			if (response.ok === false) {
				console.log(response)
				throw Error(response)
			}
			return response.json()
		})
		.then(json => {
			localStorage.setItem('accessToken', json.token)
			localStorage.setItem('user', JSON.stringify(json.user))
			// localStorage.setItem('role', JSON.stringify(json.role))
			// localStorage.setItem('company', JSON.stringify(json.company))

			setAccessToken(json.token);
			setUser(JSON.stringify(json.user));
			setIsAuthenticated(true);

			// dispatch({
			// 	type: REGISTER.SUCCESS,
			// 	payload: json,
			// })
		})
		.catch(error => {
			// console.log(error, error.message)
			setError(error.message);
			// dispatch({
			// 	type: REGISTER.ERROR,
			// 	payload: error,
			// })
		})
	}

	const requestReset = data => {
		return requestResetLink(data).then(response => {
			if (response.ok === false) {
				console.log(response)
				throw Error(response)
			}
			return response;
		})
		.then(json => {
			return json;
		})
		.catch(error => {
			setError(error.message);
		})
	}

	const applyReset = data => {
		return authReset(data).then(response => {
			if (response.ok === false) {
				console.log(response)
				throw Error(response)
			}
			return response.json();
		})
		.then(json => {
			return json;
		})
		.catch(error => {
			setError(error.message);
		})
	}

	const clearError = () => setError('');

	const syncUserCompany = (companyData) => {
		setUser((prev) => {
			const updatedUser = mergeCompanyIntoUser(prev, companyData)
			if (updatedUser && updatedUser !== prev) {
				localStorage.setItem('user', JSON.stringify(updatedUser))
			}
			return updatedUser
		})
	}

	return(
		<JobAppContext.Provider
			value={{
				auth,
				signOut,
				register,
				requestReset,
				applyReset,
				error,
				clearError,

				isLogginIn,
				isSigningOut,
				isAuthenticated,
				accessToken,
				setAccessToken,
				user,
				setUser,
				syncUserCompany,
				apiKey,
			}}
		>
			{props.children}
		</JobAppContext.Provider>
	)
}